import { type FC, useMemo, useState } from "react";

import { Button } from "@snapchat/snap-design-system";
import { FormikProvider, useFormik } from "formik";
import { useIntl } from "react-intl";

import { myLensesActionType } from "../../analytics/event-creators/createActionEvent";
import { useBlizzardEvents } from "../../analytics/hooks/useBlizzardEvents";
import { Card, CardTitle, PageHeader, PageSubHeader } from "../../layout/components";
import { usePreviewWebLensOnDevice } from "../hooks/usePreviewWebLensOnDevice";
import { useWebLensLogoMeasurement } from "../hooks/useWebLensLogoMeasurement";
import { LOGO_MAX_BYTES, LOGO_MAX_DIMENSION_PX, PUSH_ARCHIVE_MAX_BYTES, type SubmissionCheck } from "../model";
import {
    buildLogoChecks,
    buildLogoDimensionCheck,
    describePackageTypeFailure,
    describePushArchiveSizeFailure,
    formatMegabytes,
    isLogoAccepted,
    isLogoDimensionAccepted,
    isPushArchiveSizeAccepted,
    isZipPackage,
} from "../validation/preChecks";
import { type WebLensPushFormValues, webLensPushFormSchema } from "../validation/webLensFormSchemas";

import { SubmissionChecklist } from "./SubmissionChecklist";
import styles from "./WebLensPage.module.css";
import { WebLensIconControl } from "./fields/WebLensIconControl";
import { WebLensNameField } from "./fields/WebLensNameField";
import { WebLensPackageDropzone } from "./fields/WebLensPackageDropzone";
import {
    DetailsSectionTitle,
    LogoHint,
    PackageSectionTitle,
    PushAgainCta,
    PushCta,
    PushNameHint,
    PushPageSubtitle,
    PushPageTitle,
    PushRailHelper,
    PushRailTitle,
    PushSuccessBody,
    PushSuccessTitle,
    PushingCta,
    UploadLogoCta,
    messages,
} from "./messages.generated";

/**
 * Push to Device: try an UNSUBMITTED web-app build in Snapchat on the creator's own phone — the
 * web-lens analog of Lens Studio's "Preview Lens" button. Everything here is ephemeral: no lens is
 * created in any Lens Folder, nothing enters review, and the preview reaches only the Snapchat
 * account the creator is signed in with (a Team Snapchat notification, then the Lens carousel).
 * That is why the page has no folder, visibility, Lens+, game or preview-media affordances — those
 * all belong to a submission — and why success is a terminal rail state rather than a navigation:
 * there is no lens page to go to.
 *
 * Unlike submission, the zip→lens conversion runs INSIDE the push call, so a build that can't
 * convert fails right here with the conversion's reason on its rail row — the fast feedback loop
 * is the feature. The name is required — the platform's only fallback is the literal placeholder
 * "Lens", not a real default — and is held to the submission rules, so what previews well also
 * submits unchanged. The icon is optional; the conversion bakes in a default when it is absent.
 *
 * Field state lives in Formik (useFormik + FormikProvider, the flow's shared idiom); the files
 * stay component state, since they are not form fields.
 */
export const WebLensPushToDevicePage: FC = () => {
    const intl = useIntl();
    const { sendBlizzardEvent } = useBlizzardEvents();
    const [file, setFile] = useState<File | null>(null);
    const [logo, setLogo] = useState<File | null>(null);
    const logoMeasurement = useWebLensLogoMeasurement(logo);
    const { checks, result, pushing, push, reset } = usePreviewWebLensOnDevice();

    const formik = useFormik<WebLensPushFormValues>({
        initialValues: { name: "" },
        validationSchema: webLensPushFormSchema,
        validateOnMount: true,
        onSubmit: (formValues) => {
            if (!file) {
                return;
            }
            sendBlizzardEvent({
                event: "MYLENSES_ACTION",
                actionType: myLensesActionType.submit,
                label: "web_lens_push_to_device",
                namespace: "lenses",
            });
            void push(file, logo, formValues.name, logoMeasurement);
        },
    });

    // Same contract as submission: every row the rail can turn red also disables the button, so a
    // red row never sits next to an enabled button that fails for exactly that reason. No build
    // budget here — push-to-device runs the relaxed 50 MiB pipeline (see PUSH_ARCHIVE_MAX_BYTES).
    const canPush =
        !!file &&
        isZipPackage(file) &&
        isPushArchiveSizeAccepted(file) &&
        formik.isValid &&
        (!logo || isLogoAccepted(logo)) &&
        // Dimension gate, open while unmeasured — see isLogoDimensionAccepted.
        isLogoDimensionAccepted(logoMeasurement) &&
        !pushing;

    // The live readiness list — the push pre-check rows with "pending" while no file is picked,
    // positioned as runPushClientPreChecks orders them so rows keep their places when push()
    // takes the rail over. The name is a form field, so its validation reports inline rather than
    // as a rail row; the icon is optional, so its absence is not an unfinished step.
    const readinessChecks = useMemo<SubmissionCheck[]>(
        () => [
            {
                id: "package-type",
                label: intl.formatMessage(messages.checkPackageTypeLabel),
                state: file ? (isZipPackage(file) ? "pass" : "fail") : "pending",
                detail: file ? describePackageTypeFailure(intl, file) : undefined,
            },
            {
                id: "package-size",
                label: intl.formatMessage(messages.checkPackageSizeLabel, {
                    limit: formatMegabytes(PUSH_ARCHIVE_MAX_BYTES),
                }),
                state: file ? (isPushArchiveSizeAccepted(file) ? "pass" : "fail") : "pending",
                detail: file ? describePushArchiveSizeFailure(intl, file) : undefined,
            },
            ...(logo ? buildLogoChecks(intl, logo) : []),
            ...(logo && logoMeasurement ? [buildLogoDimensionCheck(intl, logoMeasurement)] : []),
        ],
        [intl, file, logo, logoMeasurement],
    );
    const railChecks = useMemo(() => (checks.length > 0 ? checks : readinessChecks), [checks, readinessChecks]);

    return (
        <div className={styles.page}>
            <PageHeader>
                <PushPageTitle />
            </PageHeader>
            <PageSubHeader>
                <PushPageSubtitle />
            </PageSubHeader>

            <FormikProvider value={formik}>
                <div className={styles.columns}>
                    <div className={styles.mainColumn}>
                        <Card>
                            <div className={styles.cardBody}>
                                <div className={styles.section}>
                                    <CardTitle>
                                        <PackageSectionTitle />
                                    </CardTitle>
                                    {/* No buildOutput measurement: the 8 MiB publish budget its
                                        meter reports does not gate a push, and measuring here
                                        would put a red meter next to a build this page accepts. */}
                                    <WebLensPackageDropzone
                                        file={file}
                                        buildOutput={null}
                                        archiveMaxBytes={PUSH_ARCHIVE_MAX_BYTES}
                                        showPublishBudget={false}
                                        disabled={pushing}
                                        onPick={(picked) => {
                                            setFile(picked);
                                            reset();
                                        }}
                                    />
                                </div>

                                <div className={styles.divider} />

                                <div className={styles.section}>
                                    <CardTitle>
                                        <DetailsSectionTitle />
                                    </CardTitle>
                                    <WebLensNameField disabled={pushing} hint={<PushNameHint />} />
                                    <WebLensIconControl
                                        logo={logo}
                                        disabled={pushing}
                                        onPick={(picked) => {
                                            setLogo(picked);
                                            reset();
                                        }}
                                        cta={<UploadLogoCta />}
                                        hint={
                                            <LogoHint
                                                limit={formatMegabytes(LOGO_MAX_BYTES)}
                                                dimension={LOGO_MAX_DIMENSION_PX}
                                            />
                                        }
                                    />
                                </div>
                            </div>
                        </Card>
                    </div>

                    <div className={styles.rail}>
                        <Card>
                            <div className={styles.railBody}>
                                <CardTitle>
                                    <PushRailTitle />
                                </CardTitle>
                                <SubmissionChecklist checks={railChecks} />
                                <div className={styles.divider} />
                                {result ? (
                                    <div className={styles.pushSuccess} data-testid="web-lens-push-success">
                                        <div className={styles.pushSuccessTitle}>
                                            <PushSuccessTitle />
                                        </div>
                                        <PushSuccessBody />
                                    </div>
                                ) : (
                                    <div className={styles.railHelper}>
                                        <PushRailHelper />
                                    </div>
                                )}
                                <div className={styles.submitButton}>
                                    <Button
                                        type="primary"
                                        isDisabled={!canPush}
                                        onClick={() => {
                                            formik.handleSubmit();
                                        }}
                                        data-testid="web-lens-push-submit"
                                    >
                                        {pushing ? <PushingCta /> : result ? <PushAgainCta /> : <PushCta />}
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </FormikProvider>
        </div>
    );
};
